import { Component, ElementRef, inject, OnDestroy, signal, viewChild, WritableSignal } from '@angular/core';
import { Avatar } from 'primeng/avatar';
import { Drawer } from 'primeng/drawer';
import { Emily } from '../../../services/emily';
import { ChatStreamEvent, EmilyChatError, EmilyChatService } from '../../../services/emily-chat';
import { FormsModule } from '@angular/forms';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { Badge } from 'primeng/badge';
import { Divider } from 'primeng/divider';
import { Skeleton } from 'primeng/skeleton';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { EmilySegmentsPipe } from '../../../pipes/emily-segments';

/**
 * Support code shown to the user (and logged) for a failed Emily turn, so a customer can report
 * the cause without opening devtools. See {@link ERROR_TEXT} for the mapping.
 */
type EmilyErrorCode = 'E-STREAM' | 'E-SESSION' | 'E-NETWORK' | 'E-SERVER' | 'E-ACCESS' | 'E-UNKNOWN';

/**
 * User-facing error text per support code. Each message ends with its code so a customer can
 * report the cause verbatim ("it said E-NETWORK") without opening devtools — the generic
 * "couldn't respond" text alone made every failure look identical in support tickets.
 *
 * - `E-STREAM`  — mid-stream model/tool failure; arrives as an SSE `error` event with HTTP 200.
 * - `E-SESSION` — 404, the chat session no longer exists; `#sessionId` is cleared alongside.
 * - `E-NETWORK` — status 0: network, CORS, a proxy cutting the SSE connection, or an abort.
 * - `E-SERVER`  — 5xx, including a gateway timeout on a long-running query.
 * - `E-ACCESS`  — 403, caller lacks the role/permission (or CSRF on a dead session).
 * - `E-UNKNOWN` — anything else (e.g. an unexpected 4xx).
 *
 * 401 never reaches here: `authInterceptor` closes the drawer and routes to `/logout` first.
 */
const ERROR_TEXT: Record<EmilyErrorCode, string> = {
  'E-STREAM':
    '⚠️ Emily ran into a problem while answering. Try rephrasing your question or ask again. (E-STREAM)',
  'E-SESSION': '⚠️ This conversation expired. Send your message again to start a new one. (E-SESSION)',
  'E-NETWORK':
    '⚠️ The connection dropped before Emily could finish. Check your network and try again. (E-NETWORK)',
  'E-SERVER': '⚠️ Emily is temporarily unavailable. Please try again in a moment. (E-SERVER)',
  'E-ACCESS':
    "⚠️ You don't have access to execute this chat. Please re-login or contact your admin. (E-ACCESS)",
  'E-UNKNOWN': "⚠️ Sorry, I couldn't respond just now. Please try again. (E-UNKNOWN)",
};

/** Map an HTTP status from the Observable's error channel to its support code. */
function errorCode(status: number): EmilyErrorCode {
  if (status === 0) return 'E-NETWORK';
  if (status === 403) return 'E-ACCESS';
  if (status === 404) return 'E-SESSION';
  if (status >= 500) return 'E-SERVER';
  return 'E-UNKNOWN';
}

@Component({
  selector: 'ems-emily-drawer',
  imports: [Avatar, Drawer, FormsModule, Textarea, Button, Badge, Divider, Skeleton, Menu, EmilySegmentsPipe],
  templateUrl: './emily-drawer.html',
  styleUrl: './emily-drawer.scss',
  standalone: true,
})
export class EmilyDrawer implements OnDestroy {
  emily: Emily = inject(Emily);
  #chat: EmilyChatService = inject(EmilyChatService);
  #toast: MessageService = inject(MessageService);

  messages: WritableSignal<Message[]> = signal([]);
  thinking: WritableSignal<boolean> = signal(true);

  input: string = '';
  // Pre-defined prompt suggestions: the chip shows `title`; clicking loads the full
  // `prompt` into the input so the user can edit it before sending.
  buttons: { title: string; prompt: string }[] = [
    {
      title: 'Students at academic risk',
      prompt: 'Show a list of all students at academic risk who scored below 70% on the "" exam in 2026.',
    },
    { title: 'Room utilization trends', prompt: 'Show room utilization trends for the last three years' },
    {
      title: 'Room utilization forecast',
      prompt: 'Provide a three-year forecast of room utilization based on current trends.',
    },
    {
      title: 'Under & over-utilized rooms',
      prompt: 'Show a list of the top five underutilized rooms and top five overutilized rooms for 2026.',
    },
    {
      title: 'Communication checklist scores',
      prompt:
        'Show the communication checklist items with the lowest average aggregated scores for standardized patient cases from January to June 2026.',
    },
    {
      title: 'Learner contact hours',
      prompt:
        'Give me total student hours in the simulation center for 2026 year-to-date, displayed in a grid by session, department, and learner.',
    },
    {
      title: 'Learner utilization summary',
      prompt: 'How many learners used the simulation center from January 2026 to today?',
    },
    {
      title: 'Faculty & SP scoring trends',
      prompt: 'Show scoring trends for faculty and standardized patients, ranked from highest to lowest scores.',
    },
    {
      title: 'Highest & lowest scenario scores',
      prompt:
        'Show the scenarios with the highest and lowest learner checklist performance scores from January to June 2026.',
    },
    {
      title: 'OSCE scores by case & category',
      prompt: 'List the OSCE scores by case and category for "".',
    },
    {
      title: 'Scenario reuse frequency',
      prompt: 'Show scenario reuse frequency over time across all available data.',
    },
    {
      title: 'Standardized patient hours/month',
      prompt: 'Show standardized patient hours from "" to "" as a monthly line chart.',
    },
  ];

  // Prompt-suggestion bar is expanded by default and toggled via the header.
  tryExpanded: WritableSignal<boolean> = signal(true);
  toggleTry = (): void => this.tryExpanded.update((v) => !v);

  private emilyInput = viewChild<ElementRef<HTMLTextAreaElement>>('emilyInput');

  // Load a suggested prompt into the input for the user to edit — do NOT send.
  fillInput = (prompt: string): void => {
    this.input = prompt;
    setTimeout(() => this.emilyInput()?.nativeElement.focus());
  };

  #sessionId: number | null = null;
  #sub?: Subscription;

  private bottomAnchor = viewChild<ElementRef<HTMLElement>>('bottomAnchor');

  // Jump to the latest message after the DOM has rendered the new content.
  #scrollToBottom = (): void => {
    setTimeout(() => {
      this.bottomAnchor()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  copyTable = (text: string, format: 'Excel' | 'CSV'): void => {
    this.#writeClipboard(text).then((ok) => {
      this.#toast.add(
        ok
          ? {
              severity: 'success',
              summary: 'Copied',
              detail: format === 'CSV' ? 'Table copied as CSV' : 'Table copied for Excel',
            }
          : { severity: 'error', summary: 'Copy failed', detail: 'Could not copy the table' },
      );
    });
  };

  // Overflow-menu items for a table's copy control. Excel first (default intent).
  copyMenu = (tsv: string, csv: string): MenuItem[] => [
    { label: 'Copy for Excel', icon: 'pi pi-file-excel', command: () => this.copyTable(tsv, 'Excel') },
    { label: 'Copy as CSV', icon: 'pi pi-file', command: () => this.copyTable(csv, 'CSV') },
  ];

  // navigator.clipboard exists only in secure contexts (https/localhost); on a plain-HTTP
  // origin (LAN IP, UAT without TLS) it is undefined, so fall back to a temp-textarea + execCommand.
  #writeClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to the legacy path
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  // Enter sends; Shift+Enter inserts a newline.
  onEnterKey = (event: Event): void => {
    if ((event as KeyboardEvent).shiftKey) {
      return;
    }
    event.preventDefault();
    this.sendInput();
  };

  sendInput = (input?: string) => {
    const value = (input ?? this.input).trim();
    if (!value || this.thinking()) {
      return;
    }
    if (!input) {
      this.input = '';
    }
    this.#addMessage({ text: value, sender: 'user' });
    this.#triggerEmily(value);
  };

  #addMessage = (message: Message) => {
    this.messages.update((messages) => [...messages, message]);
    if (message.sender == 'emily') {
      this.thinking.set(false);
    }
    this.#scrollToBottom();
  };

  #triggerEmily = (query: string) => {
    this.thinking.set(true);
    this.#scrollToBottom(); // keep the typing skeleton in view
    this.#sub?.unsubscribe();

    const stream$ =
      this.#sessionId == null
        ? this.#chat.createSession(query)
        : this.#chat.sendMessage(this.#sessionId, query);

    this.#sub = stream$.subscribe({
      next: (event: ChatStreamEvent) => {
        switch (event.type) {
          case 'session':
            this.#sessionId = event.sessionId;
            break;
          case 'chunk':
            this.#appendChunk(event.text);
            break;
          case 'done':
            this.#endStream();
            break;
          case 'error':
            console.error('Emily chat stream error:', event.message);
            this.#showError(ERROR_TEXT['E-STREAM']);
            break;
        }
      },
      error: (error: Error) => {
        // 401 (dead session) is handled app-wide by authInterceptor (routes through /logout).
        // 403 means the caller lacks access to run this chat (missing role/permission, or CSRF on a
        //   dead session) — show distinct guidance and keep the session; a blanket redirect is wrong
        //   because a 403 can hit a validly authenticated user.
        // 404 is Emily-specific: the chat session no longer exists → abandon it so the next message
        //   opens a fresh one.
        // Transient failures (network=0, timeout, 5xx, Bedrock throttling) keep the session so the
        //   conversation context (and a follow-up like a bare "2") survives a retry.
        // A non-EmilyChatError has no HTTP status at all (an unexpected client-side throw), so it
        // must not be folded into status 0 — that would mislabel a code bug as a network drop.
        const status = error instanceof EmilyChatError ? error.status : null;
        console.error(`Emily chat error (status ${status ?? 'n/a'}):`, error);
        if (status === 404) {
          this.#sessionId = null;
        }
        this.#showError(ERROR_TEXT[status === null ? 'E-UNKNOWN' : errorCode(status)]);
      },
    });
  };

  // Append a streamed token to the active Emily message, creating it on the first chunk.
  #appendChunk = (text: string) => {
    this.thinking.set(false);
    this.messages.update((messages) => {
      const last = messages[messages.length - 1];
      if (last?.sender === 'emily' && last.streaming) {
        const updated = { ...last, text: last.text + text };
        return [...messages.slice(0, -1), updated];
      }
      return [...messages, { text, sender: 'emily', streaming: true }];
    });
  };

  #endStream = () => {
    this.thinking.set(false);
    this.messages.update((messages) => {
      const last = messages[messages.length - 1];
      if (last?.sender === 'emily' && last.streaming) {
        return [...messages.slice(0, -1), { ...last, streaming: false }];
      }
      return messages;
    });
  };

  // Stop the thinking skeleton and drop an assistant bubble carrying the cause-specific text.
  #showError = (displayText: string) => {
    this.#endStream();
    this.#addMessage({ text: displayText, sender: 'emily' });
  };

  init = (): void => {
    setTimeout(() => {
      this.#addMessage({
        text: "Hi! I'm Emily, your AI assistant 👋\n\nI can help you prep for upcoming simulations, check what's due, find study resources, and more. What can I help you with today?",
        sender: 'emily',
      });
    }, 1000);
  };

  clear = (): void => {
    this.#sub?.unsubscribe();
    this.#sessionId = null;
    this.tryExpanded.set(true); // fresh session reopens with chips expanded
    setTimeout(() => {
      this.messages.set([]);
      this.thinking.set(true);
    }, 500);
  };

  ngOnDestroy(): void {
    this.#sub?.unsubscribe();
  }
}

type Message = {
  text: string;
  sender: 'emily' | 'user';
  streaming?: boolean;
};
